import { Check, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { requireUser } from "@/lib/requireUser";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useState, useRef } from "react";
import { membershipService } from "@/data/membership";

interface PricingPlan {
  name: string;
  price?: number | null;
  originalPrice?: number;
  setup_fee?: string | null;
  features: {
    [key: string]: any;
    affiliate_url?: string;
    affiliate_network?: string;
    payout_per_quote?: string;
    highlights?: string[];
  };
}

interface PricingTableProps {
  plans: PricingPlan[];
  onSelectPlan: (index: number) => void;
  isService?: boolean;
  duration?: string;
}

export const PricingTable = ({ plans, onSelectPlan, isService = false, duration = 'mo', product }: PricingTableProps & { product?: any }) => {
  const { user, membership } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const isPro = membershipService.isPro(membership);
  
  // Helper to calculate Pro price for a plan
  const getProPlanPrice = (planPrice: number): number | null => {
    if (!isPro || !product?.pro_instant_percent) return null;
    const percent = Math.min(Math.max(product.pro_instant_percent, 0), 50);
    return Math.round(planPrice * (100 - percent) / 100);
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('en-US');
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const isMobile = window.innerWidth < 640;
      const scrollAmount = isMobile ? window.innerWidth * 0.75 : 340; // Card width + gap
      const newScrollLeft = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleSelectPlan = (index: number, plan: PricingPlan) => {
    // If plan has affiliate URL, open it in new tab
    if (plan.features.affiliate_url) {
      window.open(plan.features.affiliate_url, '_blank', 'noopener,noreferrer');
      return;
    }
    
    if (isService) {
      onSelectPlan(index);
    } else {
      requireUser({
        user,
        navigate,
        currentPath: location.pathname,
        action: () => onSelectPlan(index),
      });
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="text-center px-4">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 pt-8 md:pt-12">
          {isService ? 'Select your plan to get started' : 'Choose the plan that\'s right for you'}
        </h2>
      </div>

      <div className="relative">
        {/* Left scroll arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="hidden md:block absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-xl hover:bg-primary/90 transition-all hover:scale-110 animate-pulse"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-8 w-8" strokeWidth={3} />
          </button>
        )}
        
        {/* Right scroll arrow - Desktop */}
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="hidden md:block absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-xl hover:bg-primary/90 transition-all hover:scale-110 animate-pulse"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-8 w-8" strokeWidth={3} />
          </button>
        )}
        
        {/* Mobile scroll indicator */}
        {showRightArrow && (
          <div className="md:hidden absolute right-2 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
            <div className="bg-primary text-primary-foreground rounded-full p-2 shadow-lg animate-pulse">
              <ArrowRight className="h-5 w-5" strokeWidth={3} />
            </div>
          </div>
        )}

        <div 
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory md:snap-none"
        >
          <div className="inline-flex gap-3 md:gap-6 px-4 md:px-4">
            {plans.map((plan, index) => {
              const isCustomPricing = !plan.price || plan.price === 0;
              const isAffiliate = !!plan.features.affiliate_url;
              const affiliateHighlights = plan.features.highlights || [];
              
              return (
                <Card key={index} className="flex-shrink-0 w-[75vw] sm:w-[320px] md:w-[320px] p-3 sm:p-5 md:p-6 flex flex-col hover:shadow-hover transition-shadow hover-scale snap-start">
                  <div className="text-center mb-2 md:mb-4 pb-2 md:pb-4 border-b">
                    <h3 className="font-bold text-sm sm:text-base mb-2 md:mb-3">{plan.name}</h3>
                    {plan.features.payout_per_quote && (
                      <Badge variant="default" className="mb-2">{plan.features.payout_per_quote} per quote</Badge>
                    )}
                    
                    {isCustomPricing ? (
                      <div className="mb-2">
                        <div className="text-2xl sm:text-3xl font-bold mb-2">Custom Pricing</div>
                        <Badge variant="secondary" className="mb-2 text-xs">Enterprise</Badge>
                        <p className="text-xs sm:text-sm text-muted-foreground">Contact for pricing</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-baseline justify-center gap-1 sm:gap-2 mb-2">
                          <span className="text-2xl sm:text-3xl md:text-4xl font-bold">${formatPrice(plan.price!)}</span>
                          <span className="text-sm sm:text-base md:text-lg text-muted-foreground">/{duration}</span>
                          {plan.originalPrice && plan.originalPrice > plan.price! && (
                            <span className="text-xs sm:text-sm md:text-base text-muted-foreground line-through">
                              ${formatPrice(plan.originalPrice)}
                            </span>
                          )}
                        </div>
                        {plan.originalPrice && plan.originalPrice > plan.price! && (
                          <div className="space-y-1">
                            <div className="text-xs sm:text-sm text-green-600 font-semibold">
                              Save ${formatPrice(plan.originalPrice - plan.price!)}/{duration}
                            </div>
                            {duration === 'mo' && (
                              <div className="text-sm sm:text-base md:text-lg text-green-700 font-bold">
                                ${formatPrice((plan.originalPrice - plan.price!) * 12)}/year total savings
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Pro pricing */}
                        {isPro && product?.pro_discount_mode === 'instant' && (() => {
                          const proPrice = getProPlanPrice(plan.price!);
                          const savings = plan.price! - (proPrice || plan.price!);
                          return proPrice && savings > 0 ? (
                            <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 p-2 rounded mb-2">
                              <Badge className="bg-emerald-600 text-white mb-1 text-xs">PRO</Badge>
                              <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                                ${formatPrice(proPrice)}
                                <span className="text-sm text-muted-foreground">/{duration}</span>
                              </div>
                              <div className="text-xs text-emerald-600 dark:text-emerald-500">
                                You save ${formatPrice(savings)}/{duration}
                              </div>
                            </div>
                          ) : null;
                        })()}
                        
                        {/* Non-Pro teaser */}
                        {!isPro && product?.pro_discount_mode === 'instant' && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Join Pro to save ~${formatPrice(Math.round(plan.price! * 0.1))}/{duration}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {plan.setup_fee && (
                    <div className="mb-3 text-center">
                      <Badge variant="outline" className="text-xs">
                        Setup: {plan.setup_fee}
                      </Badge>
                    </div>
                  )}

                  <Button
                    onClick={() => handleSelectPlan(index, plan)}
                    className="w-full mb-4 font-bold rounded-full text-sm sm:text-base"
                    size="lg"
                  >
                    {isAffiliate ? 'Buy Now →' : (isCustomPricing ? 'Apply Now' : (isService ? 'Book a Call' : 'Get Started'))}
                  </Button>

                  <div className="space-y-2 md:space-y-4 flex-1">
                    {isAffiliate && affiliateHighlights.length > 0 ? (
                      affiliateHighlights.map((highlight, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs sm:text-sm py-1.5">
                          <span className="text-foreground">{highlight}</span>
                        </div>
                      ))
                    ) : (
                      Object.entries(plan.features)
                        .filter(([key]) => !['affiliate_url', 'affiliate_network', 'payout_per_quote', 'conversion_rate', 'highlights', 'coverage_details'].includes(key))
                        .map(([key, value]) => (
                          <div key={key} className="py-3 border-b last:border-b-0">
                            <div className="text-xs font-semibold text-foreground mb-1">{key}</div>
                            {typeof value === "boolean" ? (
                              value ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 rounded-full bg-accent flex items-center justify-center shrink-0">
                                    <Check className="h-3 w-3 text-white" />
                                  </div>
                                  <span className="text-xs text-muted-foreground">Included</span>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">Not included</span>
                              )
                            ) : (
                              <span className="text-sm text-muted-foreground">{value}</span>
                            )}
                          </div>
                        ))
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};